import { billingEn } from "./billing.en";
import { hrEn } from "./hr.en";
import { dashboardEn } from "./dashboard.en";
import { paymentsEn } from "./payments.en";
import { reportsEn } from "./reports.en";
import type { MessageId } from "./fr";

/** English catalog. Same keys and {placeholders} as fr.ts (checked by catalogs.t.ts). */
// Terms: feature (fonctionnalité), feature enablement = "feature", role, case (dossier), pro, customer (client), request (demande), lead (opportunité), reason (motif), show/reveal (afficher), team member (membre de l'équipe), sign out, end session (couper la session), trade (métier).
export const en: Record<MessageId, string> = {
  ...reportsEn,
  ...paymentsEn,
  ...dashboardEn,
  ...billingEn,
  ...hrEn,
  // ── Brand and shell ──
  "brand.name": "Walliyo",
  "brand.initial": "W",
  "brand.console": "Console",
  "nav.aria": "Console",
  "nav.new": "NEW",
  "nav.group.configuration": "Configuration",
  "overview.eachDepartment": "Each department, one line",
  "overview.dept.marketplace": "Marketplace Ops",
  "overview.dept.growth": "Sales & Growth",
  "overview.dept.support": "Customer Support",
  "overview.dept.trust": "Trust & Safety",
  "overview.dept.finance": "Finance",
  "overview.dept.people": "Human Resources",
  "overview.dept.administration": "Administration",
  "overview.dept.product": "Engineering",
  "overview.clickThrough": "click through to theirs",
  "overview.numberToWatch": "The number to watch",
  "overview.whereWalliyoWorks": "Where Walliyo works",
  "overview.noCitiesYet": "No jobs this month.",
  "overview.points": "{points} points",
  "overview.title.people.walliyoMonth": "Walliyo, {month}",
  "overview.people.tab.waiting": "Waiting on you",
  "overview.people.tab.company": "The company",
  "overview.people.tab.weeks": "The weeks ahead",
  "overview.people.askForTimeOff": "Ask for time off",
  "overview.people.joinedThisMonth": "{count} joined this month",
  "overview.people.joinedThisMonth.one": "1 joined this month",
  "overview.people.candidates": "{count} candidates",
  "overview.people.candidates.one": "1 candidate",
  "overview.people.medianStay": "Years, median stay",
  "overview.people.whereEverybodyIs": "Where everybody is",
  "overview.people.oneLinePerTeam": "one line per team",
  "overview.people.noTeamsYet": "No teams yet.",
  "overview.people.noLead": "no lead",
  "overview.people.nobodyCovering": "Nobody covering",
  "overview.people.thinWeek": "{team} loses {away} of {size} in the week of {week}.",
  "overview.people.whoIsWhere": "Who is where",
  "overview.people.fourWeeks": "four weeks, and what lands in each",
  "overview.people.allTimeOff": "All time off",
  "overview.people.quietWeeks": "Nothing lands in the next four weeks.",
  "overview.people.mark.payrollCloses": "payroll closes {on}",
  "overview.people.mark.reviewsClose": "reviews close {on}",
  "overview.people.span.leave": "{days} days off",
  "overview.people.span.leave.one": "1 day off",
  "overview.people.span.leavePending": "{days} days · undecided",
  "overview.people.span.leavePending.one": "1 day · undecided",
  "overview.people.span.starts": "starts",
  "overview.people.task.candidates": "{count} candidates waiting on you",
  "overview.people.detail.candidates": "across {second} open roles",
  "marketplace.seeAll": "See all {count}",
  "marketplace.findAPro": "Find a pro",
  "marketplace.theMarket": "The market, this week",
  "overview.title.marketplace.theMarket": "The market, this week",
  "overview.lede.marketplace.nobodyAnswered": "{first} people asked for something and nobody answered.",
  "overview.lede.marketplace.demandNoSupply": "{first} trades are asked for where no pro holds the skill.",
  "overview.lede.marketplace.quiet": "Nothing was posted this week.",
  "overview.lede.marketplace.allAnswered": "Every job posted this week got a quote.",
  "overview.people.link.employees": "Employees",
  "overview.people.link.recruiting": "Recruiting",
  "overview.people.link.learning": "Learning",
  "overview.people.link.teams": "Teams",
  "overview.administration.askedNotAnswered": "Asked for, not answered",
  "overview.administration.oldestFirst": "oldest first",
  "overview.administration.nobodyWaiting": "Nobody is waiting on an answer.",
  "overview.administration.row.wantsUntil": "{subject} wants to {asked}",
  "overview.administration.row.wantsForever": "{subject} wants to {asked}, with no end date",
  "overview.administration.until": "until {on}",
  "overview.administration.noEndAsked": "no end date asked for",
  "overview.administration.hoursWaiting": "{hours} hours waiting",
  "overview.administration.hoursWaiting.one": "1 hour waiting",
  "overview.administration.pastTheDeadline": "past the deadline",
  "overview.administration.nobodyTookItBack": "Nobody took it back",
  "overview.administration.noEndDate": "{count} grants have no end date.",
  "overview.administration.noEndDate.one": "1 grant has no end date.",
  "overview.administration.belongToLeavers": "{leavers} belong to somebody who has left.",
  "overview.administration.belongToLeavers.one": "1 belongs to somebody who has left.",
  "overview.administration.reviewThem": "Review them",
  "overview.administration.criticalCapabilities": "Critical capabilities",
  "overview.administration.ifAnyReachesOne": "If any of these reaches one, nobody can cover when they are away.",
  "overview.figure.holdCritical": "Hold something critical",
  "overview.lede.administration.waitingAndGrants":
    "{first} people are waiting to be told yes or no, and {second} grants have no end date.",
  "overview.lede.administration.waiting": "{first} people are waiting to be told yes or no.",
  "overview.lede.administration.grants": "{first} grants have no end date and nobody took them back.",
  "overview.lede.administration.nothingWaiting": "Nobody is waiting on an answer.",
  "overview.growth.step.signedUp": "Signed up",
  "overview.growth.step.verified": "Verified",
  "overview.growth.step.addedSkill": "Added a skill",
  "overview.growth.step.quotedOnce": "Quoted once",
  "overview.growth.step.wonAJob": "Won a job",
  "overview.growth.lost.signedUp": "never signed up",
  "overview.growth.lost.verified": "gave up",
  "overview.growth.lost.addedSkill": "never did",
  "overview.growth.lost.quotedOnce": "never quoted",
  "overview.growth.lost.wonAJob": "never won",
  "overview.growth.stuck": "Stuck, and worth a call",
  "overview.growth.stuckWho": "verified, in a trade with demand, never quoted",
  "overview.growth.nobodyStuck": "Nobody is stuck.",
  "overview.growth.row.workGoingUnanswered": "{subject}",
  "overview.growth.why.workGoingUnanswered": "verified {days} days ago · work in their trade is going unanswered",
  "overview.growth.row.neverQuoted": "{subject}",
  "overview.growth.why.neverQuoted": "verified {days} days ago · has never bid on anything",
  "overview.growth.promoCost":
    "It has given away {given} in discounts against {earned} in fees from the pros who used it.",
  "overview.growth.promoUsed": "{used} pros have used it",
  "overview.growth.beforeYouCampaign": "Before you campaign",
  "overview.growth.notificationsAreFree":
    "In-app notifications reach these pros for nothing. SMS costs per message, and data is paid by the hour here.",
  "overview.action.call": "Call",
  "overview.lede.growth.neverQuoted": "{first} pros signed up and never quoted on anything. That is the whole job.",
  "overview.lede.growth.nobodySignedUp": "Nobody signed up this month.",
  "overview.lede.growth.allWorking": "Every pro who signed up has quoted on something.",
  "overview.support.dueInMinutes.one": "due in 1 minute",
  "overview.support.dueInHours.one": "due in 1 hour",
  "overview.support.overdue.one": "1 minute past its deadline",
  "overview.support.nobodyOwnsWhy.one":
    "A case nobody owns is a case nobody is answering. The oldest has been sitting for 1 day.",
  "overview.people.days.one": "1 day",
  "overview.people.payrollOpen.one": "{month} is open. It closes tomorrow.",
  "overview.people.detail.leave.one": "Asked 1 day ago · you are their lead",
  "overview.finance.waiting.one": "1 day waiting",
  "overview.trust.row.appealWaiting.one": "Appeal waiting 1 day",
  "overview.trust.legalHoldsWhy.one":
    "1 account is held. Nothing about it can be deleted, including by a data request.",
  "overview.product.unconfirmed.one": "1 callback unconfirmed, {minutes} minutes ago.",
  "overview.product.whatIsBroken": "What is broken",
  "overview.product.worstFirst": "worst first",
  "overview.product.nothingBroken": "Nothing is broken.",
  "overview.product.unconfirmed": "{count} callbacks unconfirmed, the oldest {minutes} minutes ago.",
  "overview.product.unconfirmedWhy": "Each is a pro who paid and is not marked paid.",
  "overview.product.replayThem": "Replay them",
  "overview.product.rolledOut": "Rolled out to customers",
  "overview.product.tally.everybody": "Everybody",
  "overview.product.tally.someAccounts": "Some accounts",
  "overview.product.tally.off": "Off",
  "overview.figure.rolloutsInFlight": "Rollouts in flight",
  "overview.product.row.incidentOpen": "{subject}",
  "overview.product.why.incidentOpen": "open {amount} · customers can see the banner",
  "overview.product.row.ruleScheduled": "Rule takes effect {at}",
  "overview.product.why.ruleScheduled": "{subject} {amount} · nobody has signed off",
  "overview.product.row.experimentRunning": "{subject} at {days}% of customers",
  "overview.product.why.experimentRunning": "running since {on} · ready to decide about",
  "overview.figure.incidentsOpen": "Incidents open",
  "overview.lede.product.incidentAndCallbacks": "{first} incident is open, and {second} payment callbacks failed.",
  "overview.lede.product.incident": "{first} incident is open.",
  "overview.lede.product.callbacks": "{first} payment callbacks failed and nobody has replayed them.",
  "overview.lede.product.rulesScheduled": "{first} rules take effect with nobody present.",
  "overview.lede.product.nothingBroken": "Nothing is broken.",
  "overview.finance.aboveLimit": "Above somebody's limit",
  "overview.finance.theyAskedYouDecide": "they asked, you decide",
  "overview.finance.nothingWaitingHere": "Nothing is waiting on an approval.",
  "overview.finance.theirLimit": "limit {ceiling}",
  "overview.finance.noLimit": "holds no limit",
  "overview.finance.waiting": "{days} days waiting",
  "overview.finance.today": "today",
  "overview.finance.subscriptions": "Subscriptions",
  "overview.finance.tally.endingSoon": "Renewing in 7 days",
  "overview.finance.tally.suspended": "Payment failed",
  "overview.finance.tally.ended": "Ended, not renewed",
  "overview.finance.promosRunning": "Promos running",
  "overview.finance.promoEnds": "{name} ends {on}.",
  "overview.finance.promoNoEnd": "{name} has no end date.",
  "overview.finance.promoCost": "It has given away {given} in discounts against {earned} in fees.",
  "overview.figure.feesCollected": "Fees collected",
  "overview.figure.jobsBilled": "Jobs billed",
  "overview.figure.renewalsFailed": "Renewals failed",
  "overview.lede.finance.waitingOnApproval": "{first} corrections are waiting on somebody who can approve them.",
  "overview.lede.finance.renewalsFailed": "{first} renewals failed and nobody has taken them up.",
  "overview.lede.finance.renewingSoon": "{first} subscriptions renew within the week.",
  "overview.lede.finance.nothingWaiting": "Nothing is waiting on an approval.",
  "overview.trust.needsDecision": "Needs a decision today",
  "overview.trust.mostSeriousFirst": "most serious first",
  "overview.trust.nothingUrgentHere": "Nothing needs a decision today.",
  "overview.trust.verificationQueue": "Verification queue",
  "overview.trust.whyVerify": "A pro waiting to be verified cannot be found or hired.",
  "overview.trust.tally.underADay": "Waiting under 24h",
  "overview.trust.tally.overADay": "Over 24h",
  "overview.trust.legalHolds": "Legal holds",
  "overview.trust.legalHoldsWhy":
    "{count} accounts are held. Nothing about them can be deleted, including by a data request.",
  "overview.trust.row.criticalUnreferred": "{subject}",
  "overview.trust.why.criticalUnreferred": "critical · not referred to police",
  "overview.trust.row.restrictionExpiring": "Restriction lifts itself on {on}",
  "overview.trust.why.restrictionExpiring": "{subject} · lift it, or it lifts itself",
  "overview.trust.row.appealWaiting": "Appeal waiting {days} days",
  "overview.trust.why.appealWaiting": "a restricted account asked to be reconsidered",
  "overview.figure.underLegalHold": "Under legal hold",
  "overview.lede.trust.criticalUnreferred": "{first} reports are critical and none has been referred yet.",
  "overview.lede.trust.waitingOverADay": "{first} pros have waited more than a day to be verified.",
  "overview.lede.trust.waitingToVerify": "{first} pros are waiting to be verified.",
  "overview.lede.trust.nothingUrgent": "Nothing needs a decision today.",
  "overview.support.runningOut": "Running out of time",
  "overview.support.soonestFirst": "soonest deadline first",
  "overview.support.nothingDue": "Nothing is close to its deadline.",
  "overview.support.whereFrom": "Where they came from",
  "overview.support.noChannels": "No open cases.",
  "overview.support.channel.chat": "In the app",
  "overview.support.channel.phone": "Phone",
  "overview.support.channel.email": "Email",
  "overview.support.nobodyOwns": "{count} nobody owns",
  "overview.support.nobodyOwnsWhy":
    "A case nobody owns is a case nobody is answering. The oldest has been sitting for {days} days.",
  "overview.support.takeOldest": "Take the oldest",
  "overview.support.row.mine": "yours",
  "overview.support.row.theirs": "somebody else's",
  "overview.support.row.unclaimed": "unclaimed",
  "overview.support.dueInMinutes": "due in {minutes} minutes",
  "overview.support.dueInHours": "due in {hours} hours",
  "overview.support.overdue": "{minutes} minutes past its deadline",
  "overview.figure.toFirstReply": "To first reply",
  "overview.action.reply": "Reply",
  "overview.action.claim": "Claim",
  "overview.lede.support.breaching": "{first} cases are about to miss their deadline.",
  "overview.lede.support.breachingSomeYours":
    "{first} cases are about to miss their deadline. {second} of them are yours.",
  "overview.lede.support.unclaimed": "{first} cases are waiting for somebody to take them.",
  "overview.lede.support.nothingBreaching": "Nothing is about to miss its deadline.",
  "overview.against": "against {month}",
  "overview.title.executive.walliyoMonth": "Walliyo, {month}",
  "overview.title.people.thingsNeedYou": "{count} things need you",
  "overview.title.people.nothingNeedsYou": "Nothing needs you",
  "overview.title.support.bonjour": "Hello, {name}",
  "overview.title.trust.safetyToday": "Safety, today",
  "overview.title.finance.theMoney": "The money, this week",
  "overview.title.product.themOrUs": "Is it them, or is it us",
  "overview.title.growth.gettingProsWorking": "Getting pros working",
  "overview.title.administration.whoMayDoWhat": "Who may do what",
  "overview.title.marketplace.plain": "Overview",
  "overview.title.legal.plain": "Overview",
  "overview.lede.executive.grewFaster": "The market grew. What it could not serve grew faster.",
  "overview.lede.executive.wentUnanswered": "{first} requests went unanswered this month.",
  "overview.lede.executive.quiet": "Nothing was posted this month.",
  "overview.lede.executive.holding": "{second} requests posted, and every one of them found an answer.",
  "overview.people.allClear": "Nothing needs you. Everything has been dealt with.",
  "overview.people.theCompany": "The company",
  "overview.people.employees": "employees",
  "overview.people.teams": "teams",
  "overview.people.yours": "Yours",
  "overview.people.leaveLeft": "Leave left",
  "overview.people.days": "{days} days",
  "overview.people.noAllowance": "not set",
  "overview.people.coursesDue": "Courses due",
  "overview.people.payroll": "Payroll",
  "overview.people.payrollOpen": "{month} is open. It closes in {days} days.",
  "overview.people.payrollDue": "{month} is open and its closing date has passed.",
  "overview.people.payrollClosed": "{month} is closed.",
  "overview.people.linesChecked": "{checked} of {total} lines checked",
  "overview.people.task.leave": "{subject} wants {count} days off",
  "overview.people.detail.leave": "Asked {days} days ago · you are their lead",
  "overview.people.task.grantsNoEndDate": "{count} grants have no end date",
  "overview.people.detail.grantsNoEndDate": "{second} belong to people you lead",
  "overview.people.task.reviews": "{count} reviews are yours to write",
  "overview.people.detail.reviews": "closes {on}",
  "overview.people.task.starting": "{subject} starts on {on}",
  "overview.people.detail.starting": "role already assigned",
  "overview.people.task.startingNoRole": "{subject} starts on {on}",
  "overview.people.detail.startingNoRole": "no role assigned yet",
  "overview.action.decide": "Decide",
  "overview.action.review": "Review",
  "overview.action.write": "Write",
  "overview.action.setUp": "Set up",
  "overview.action.open": "Open",
  "overview.hero.jobsPosted": "Jobs posted",
  "overview.hero.gotAQuote": "Got a quote",
  "overview.hero.fees": "Fees, GNF",
  "overview.hero.activePros": "Active pros",
  "overview.hero.employees": "Employees",
  "overview.headline.unanswered": "{count} jobs nobody bid on · {second} categories uncovered",
  "overview.headline.neverQuoted": "{count} pros signed up and never quoted",
  "overview.headline.breaching": "{count} cases breaching · {second} unclaimed",
  "overview.headline.criticalUnreferred": "{count} critical reports not referred · {second} verifications waiting",
  "overview.headline.approvalsWaiting": "{count} corrections waiting · {second} overdue",
  "overview.headline.approvalsWaiting.zero": "{count} corrections waiting · nothing overdue",
  "overview.headline.grantsWithoutExpiry": "{count} grants with no end date · {second} access requests",
  "overview.headline.incidentsOpen": "{count} incident open · {second} callbacks failed",
  "overview.headline.startingAndPayroll": "{count} starting · payroll open",
  "overview.watch.demandOutrunningSupply":
    "Jobs are up {first}% and the share getting a quote is down {second} points. Demand is arriving faster than supply can answer it.",
  "overview.watch.nobodyAnswered": "{first} jobs were posted that nobody bid on.",
  "roles.new.department": "Department",
  "roles.new.pickDepartment": "Choose a department…",
  "roles.new.departmentNeeded":
    "Choose the role's department: a QA tester is Product & Engineering, an accountant is Finance.",
  "screen.experiments": "Experiments",
  "screen.leads": "Leads",
  "screen.campaigns": "Campaigns",
  "screen.referrals": "Referrals",
  "experiments.title": "Experiments",
  "experiments.sub": "Turn a feature on for a share of customers, and compare.",
  "experiments.loadFailed": "Experiments could not be loaded.",
  "experiments.none": "No experiment.",
  "experiments.start": "Start an experiment",
  "experiments.created": "{reference} created",
  "experiments.moved": "{reference} at {percent}%",
  "experiments.ship": "Ship it",
  "experiments.stop": "Stop",
  "experiments.sticky": "A person stays in their group: the group is worked out, never drawn.",
  "experiments.col.name": "Experiment",
  "experiments.col.feature": "Feature",
  "experiments.col.share": "Share",
  "experiments.col.status": "Status",
  "experiments.col.ramp": "Ramp",
  "experiments.status.draft": "Draft",
  "experiments.status.running": "Running",
  "experiments.status.stopped": "Stopped",
  "experiments.status.shipped": "Shipped",
  "experiments.startTitle": "Start an experiment",
  "experiments.startBody": "It starts at 0%: creating one and opening it are two decisions.",
  "experiments.startSubmit": "Create",
  "experiments.featureNeeded": "Pick a customer feature.",
  "experiments.pickFeature": "Choose…",
  "experiments.field.name": "What is being tried",
  "experiments.field.feature": "Customer feature",
  "experiments.field.featureHint": "An experiment runs on customers, never on staff.",
  "experiments.field.metric": "What is measured",
  "experiments.field.metricHint": "Read by a person, not by code.",
  "leads.title": "Leads",
  "leads.sub": "Pros we want and do not have yet.",
  "leads.loadFailed": "Leads could not be loaded.",
  "leads.none": "No lead.",
  "leads.add": "Add a lead",
  "leads.added": "{name} added",
  "leads.moved": "{name} updated",
  "leads.markContacted": "Contacted",
  "leads.markLost": "Lost",
  "leads.nameNeeded": "Give the pro's name.",
  "leads.status.new": "New",
  "leads.status.contacted": "Contacted",
  "leads.status.signed_up": "Signed up",
  "leads.status.lost": "Lost",
  "leads.source.coverage_gap": "Coverage gap",
  "leads.source.referral": "Recommendation",
  "leads.source.inbound": "Inbound",
  "leads.source.other": "Other",
  "leads.addTitle": "Add a lead",
  "leads.addBody": "Somebody not on the platform yet.",
  "leads.addSubmit": "Add",
  "leads.field.name": "Name",
  "leads.field.phone": "Phone",
  "leads.field.trade": "Trade",
  "leads.field.city": "City",
  "leads.field.source": "Source",
  "campaigns.title": "Campaigns",
  "campaigns.sub": "Messages to pros and customers.",
  "campaigns.loadFailed": "Campaigns could not be loaded.",
  "campaigns.none": "No campaign.",
  "campaigns.draft": "Draft a campaign",
  "campaigns.drafted": "{reference} ready: {count} recipients",
  "campaigns.send": "Send",
  "campaigns.sent": "Sent to {count} people",
  "campaigns.wentTo": "Went to {count}",
  "campaigns.budgetNeeded": "An SMS campaign needs a budget.",
  "campaigns.onlyInactive": "Only those who have done nothing",
  "campaigns.inAppFree": "Free: the notification arrives in the app.",
  "campaigns.smsCosts": "Paid: every SMS costs, and data here is paid by the hour.",
  "campaigns.smsLength": "{count} characters (over 160 is two SMS per person)",
  "campaigns.channel.in_app": "In the app",
  "campaigns.channel.sms": "SMS",
  "campaigns.audience.pros": "Pros",
  "campaigns.audience.customers": "Customers",
  "campaigns.draftTitle": "Draft a campaign",
  "campaigns.draftBody": "The recipient count is taken again when it is sent.",
  "campaigns.draftSubmit": "Draft it",
  "campaigns.field.name": "Name",
  "campaigns.field.channel": "Channel",
  "campaigns.field.audience": "Audience",
  "campaigns.field.body": "Message",
  "campaigns.field.bodyHint": "In French, seen by everybody.",
  "campaigns.field.budget": "Budget (GNF)",
  "campaigns.field.budgetHint": "What it is allowed to spend.",
  "referrals.title": "Referrals",
  "referrals.sub": "Pros brought by other pros.",
  "referrals.loadFailed": "Referrals could not be loaded.",
  "referrals.none": "No referral.",
  "referrals.unused": "Code unused",
  "referrals.paidOut": "{amount} {currency} paid out",
  "referrals.paidWhen": "Paid when the referred pro wins their first job.",
  "referrals.status.open": "Open",
  "referrals.status.signed_up": "Signed up",
  "referrals.status.rewarded": "Paid",
  "referrals.status.expired": "Expired",
  "screen.incidents": "Incidents",
  "incidents.title": "Incidents",
  "incidents.sub": "What is broken, and what people are being told.",
  "incidents.loadFailed": "Incidents could not be loaded.",
  "incidents.openOne": "Open an incident",
  "incidents.happeningNow": "Happening now",
  "incidents.allWell": "Everything is working.",
  "incidents.over": "Over",
  "incidents.open": "Open",
  "incidents.notAnnounced": "Not announced",
  "incidents.started": "Started {when}",
  "incidents.opened": "{reference} opened",
  "incidents.updated": "{reference} updated",
  "incidents.status.open": "Open",
  "incidents.status.monitoring": "Monitoring",
  "incidents.status.resolved": "Resolved",
  "incidents.severity.degraded": "Degraded",
  "incidents.severity.down": "Down",
  "incidents.openTitle": "Open an incident",
  "incidents.openBody":
    "A banner shows in front of every customer while this is open. Leave it empty to track the incident without telling anybody.",
  "incidents.openSubmit": "Open it",
  "incidents.titleNeeded": "Say what is broken, in a few words.",
  "incidents.causeNeeded": "Say why it happened before closing it.",
  "incidents.updateSubmit": "Save",
  "incidents.field.title": "What is broken",
  "incidents.field.severity": "Severity",
  "incidents.field.banner": "Message to customers",
  "incidents.field.bannerHint": "In French, seen by everybody. Empty: nobody is told.",
  "incidents.field.status": "Status",
  "incidents.field.note": "Note",
  "incidents.field.noteHint": "Added to the timeline, which nothing can rewrite.",
  "incidents.field.cause": "Cause",
  "screen.overview": "Overview",
  "overview.sub": "What is waiting on you today.",
  "overview.loadFailed": "The overview could not be loaded.",
  "overview.needsYou": "What needs you",
  "overview.nothingToShow": "Nothing to show here: you hold none of what this department reads.",
  "overview.waitingDays": "{days}d waiting",
  "overview.figure.leaveToDecide": "Leave to decide",
  "overview.figure.reviewsOwed": "Reviews to write",
  "overview.figure.openRoles": "Open roles",
  "overview.figure.startingSoon": "Starting this week",
  "overview.figure.payrollOpen": "Payroll runs open",
  "overview.figure.leaversStillHolding": "Leavers still holding access",
  "overview.figure.accessRequests": "Access requests",
  "overview.figure.grantsWithoutExpiry": "Grants with no end date",
  "overview.figure.rolesHeldByNobody": "Roles nobody holds",
  "overview.figure.staffWithSignIn": "Active employees",
  "overview.figure.casesBreaching": "Cases breaching soon",
  "overview.figure.casesMine": "Your cases",
  "overview.figure.casesUnclaimed": "Cases nobody owns",
  "overview.figure.casesOpen": "Cases open",
  "overview.figure.criticalUnreferred": "Critical reports not referred",
  "overview.figure.verificationsWaiting": "Verifications waiting",
  "overview.figure.verificationsOverADay": "Waiting over 24h",
  "overview.figure.reportsOpen": "Reports open",
  "overview.figure.restrictionsToReview": "Restrictions to review",
  "overview.figure.legalHolds": "Legal holds",
  "overview.figure.approvalsWaiting": "Corrections to approve",
  "overview.figure.feesThisMonth": "Payments this month",
  "overview.figure.creditsOutstanding": "Credits outstanding",
  "overview.figure.subscriptionsEndingSoon": "Subscriptions ending within 7d",
  "overview.figure.providerEventsFailed": "Provider callbacks failed",
  "overview.figure.rulesScheduled": "Rules scheduled",
  "overview.figure.featureExceptions": "Feature exceptions",
  "overview.figure.prosSignedUp": "Pros signed up (30d)",
  "overview.figure.prosApproved": "Pros approved (30d)",
  "overview.figure.prosNeverQuoted": "Pros who never quoted",
  "overview.figure.prosWithWork": "Pros who won work",
  "overview.figure.promosRunning": "Promotions running",
  "overview.figure.jobsThisMonth": "Jobs this month",
  "overview.figure.customers": "Customer accounts",
  "overview.item.caseDueOwned": "Your case is running out of time",
  "overview.item.caseDueUnclaimed": "Unowned case running out of time",
  "screen.marketplaceOverview": "Overview",
  "screen.jobs": "Jobs",
  "screen.providers": "Providers",
  "screen.categories": "Categories",
  "screen.marketplaceReviews": "Reviews",
  "marketplace.title": "The market",
  "marketplace.sub": "What was asked for, who can do it, and where nobody can.",
  "marketplace.loadFailed": "The marketplace could not be loaded.",
  "marketplace.posted": "Jobs posted",
  "marketplace.gotQuote": "Got a quote",
  "marketplace.accepted": "Accepted",
  "marketplace.noBids": "Nobody bid",
  "marketplace.toFirstQuote": "To first quote",
  "marketplace.hoursShort": "{hours}h",
  "marketplace.nobodyBid": "Nobody bid on these",
  "marketplace.nobodyBidHint": "Oldest first.",
  "marketplace.allAnswered": "Everything posted has had an answer.",
  "marketplace.waitingDays": "{days}d waiting",
  "marketplace.noProHere": "no pro holds this skill here",
  "marketplace.prosNearby": "{count} pros nearby",
  "marketplace.demandNoSupply": "Demand with no supply",
  "marketplace.demandNoSupplyHint": "Asked for in the last 30 days, with no pro holding the skill there.",
  "marketplace.everywhereCovered": "Every city has pros for what it asks for.",
  "marketplace.activePros": "{count} pros can take work today",
  "marketplace.jobs": "Jobs",
  "marketplace.jobsSub": "Every job, and how far it got.",
  "marketplace.noJobs": "No job matches.",
  "marketplace.filter.all": "All",
  "marketplace.filter.no_quotes": "No quotes",
  "marketplace.filter.requested": "Posted",
  "marketplace.filter.quoted": "Quoted",
  "marketplace.filter.accepted": "Accepted",
  "marketplace.filter.cancelled": "Cancelled",
  "marketplace.status.requested": "Posted",
  "marketplace.status.quoted": "Quoted",
  "marketplace.status.accepted": "Accepted",
  "marketplace.status.cancelled": "Cancelled",
  "marketplace.status.declined": "Declined",
  "marketplace.status.expired": "Expired",
  "marketplace.col.job": "Job",
  "marketplace.col.city": "City",
  "marketplace.col.posted": "Posted",
  "marketplace.col.quotes": "Quotes",
  "marketplace.col.estimate": "Estimate",
  "marketplace.col.status": "Status",
  "marketplace.providers": "Providers",
  "marketplace.providersSub": "Who can work, and who is not working yet.",
  "marketplace.noPros": "No provider matches.",
  "marketplace.neverQuoted": "Never quoted",
  "marketplace.allPros": "Everyone",
  "marketplace.unverified": "Unverified",
  "marketplace.col.pro": "Pro",
  "marketplace.col.skills": "Skills",
  "marketplace.col.quotesGiven": "Quotes given",
  "marketplace.col.rating": "Rating",
  "marketplace.categories": "Categories",
  "marketplace.categoriesSub": "What people ask for, and whether anybody can do it.",
  "marketplace.noCategories": "No category to show.",
  "marketplace.noDemand": "No demand",
  "marketplace.coverageHint": "Coverage is the share of jobs that got at least one quote.",
  "marketplace.col.category": "Category",
  "marketplace.col.jobs": "Jobs",
  "marketplace.col.pros": "Pros",
  "marketplace.col.coverage": "Coverage",
  "marketplace.col.uncovered": "Cities with no pro",
  "marketplace.reviews": "Reviews",
  "marketplace.reviewsSub": "What customers say about pros.",
  "marketplace.noReviews": "No review matches.",
  "marketplace.poorOnly": "3 stars or fewer",
  "marketplace.allReviews": "All",
  "marketplace.averageIs": "Average {average} over {total} reviews",
  "nav.group.everyone": "Everyone",
  "workspace.yours": "Your workspaces",
  "department.executive": "Executive",
  "department.executive.blurb": "The company, at a glance",
  "department.marketplace": "Marketplace Operations",
  "department.marketplace.blurb": "Jobs, providers, categories",
  "department.support": "Customer Support",
  "department.support.blurb": "Cases, customers and pros",
  "department.trust": "Trust & Safety",
  "department.trust.blurb": "Verifications, reports, restrictions",
  "department.finance": "Finance",
  "department.finance.blurb": "Fees, corrections, subscriptions",
  "department.growth": "Sales & Growth",
  "department.growth.blurb": "Signing up pros, promotions",
  "department.product": "Product & Engineering",
  "department.product.blurb": "Incidents, rules, rollouts",
  "department.people": "Human Resources",
  "department.people.blurb": "Hiring, employment, payroll",
  "department.administration": "Administration & Security",
  "department.administration.blurb": "Roles, permissions, audit",
  "department.legal": "Legal & Compliance",
  "department.legal.blurb": "Terms, data, legal requests",
  "nav.signedInAs": "Signed in as",
  "nav.screenCount": "{roles} · {visible} of {total} screens",
  "profile.title": "Your profile",
  "profile.open": "Open your profile",
  "profile.identity": "You",
  "profile.email": "Email",
  "profile.roles": "Roles",
  "profile.permits": "What your roles let you do",
  "profile.screensOpen": "{visible} of {total} screens open to you",
  "profile.actionCount": "{count} actions you may take",
  "profile.noPermissions": "No permissions with a limit: nothing you do needs approval.",
  "profile.preferences": "Preferences",
  "profile.emailLabel": "Email:",
  "profile.uuidLabel": "UUID:",
  "profile.rolesLabel": "Roles",
  "profile.figure.screens": "screens open to you",
  "profile.figure.actions": "actions you can take",
  "profile.figure.ceilings": "with a ceiling",
  "profile.ceilingHeading": "The most you may do alone",
  "profile.ceilingHint": "Above a ceiling the action still opens — your attempt becomes a request for approval.",
  "profile.signOutHint": "You’ll need to sign in again.",
  "header.signOut": "Sign out",
  "header.signingOut": "Signing out…",
  "header.language": "Console language",
  "header.theme": "Theme",
  "header.theme.system": "System",
  "header.theme.light": "Light",
  "header.theme.dark": "Dark",
  "header.languageNotSaved": "For this session only: this choice isn't saved to your account yet.",
  "shell.loading": "Loading the console…",
  "shell.loadFailed": "Couldn't load your profile",
  "shell.retry": "Try again",
  "shell.refreshFailed": "Your permissions couldn't be refreshed. The console is showing the ones loaded at {time}.",
  "shell.noScreens": "No screens are open to your roles ({roles}). Ask a manager for access.",
  "shell.noRoles": "No screens are open to you yet. Ask a manager to give you a role.",
  "connection.retrying": "Unstable connection · retry {attempt} of {max}…",

  // ── Screens ──
  "screen.dashboard": "Dashboard",
  "screen.myRequests": "My requests",
  "screen.myTimeOff": "My time off",
  "myTimeOff.sub": "Your balance, what you have asked for, and who else is away.",
  "myRequests.sub": "What you have asked to be allowed to do, and where each answer stands.",
  "screen.as.directory": "Directory",
  "screen.as.market": "Marketplace",
  "screen.as.employees": "Employees",
  "screen.as.recruiting": "Recruiting",
  "screen.as.learning": "Learning",
  "screen.as.payroll": "Payroll",
  "screen.as.money": "Money",
  "screen.as.workforce": "Workforce",
  "screen.as.accounts": "Accounts",
  "screen.cases": "Cases",
  "screen.customers": "Customers & pros",
  "screen.verifications": "Verifications",
  "screen.reports": "Reports",
  "screen.billing": "Prices and subscriptions",
  "screen.payments": "Payments",
  "screen.features": "Feature Enablements",
  "screen.userSettings": "User Settings",
  "settings.title": "User Settings",
  "settings.tab.app": "App user",
  "settings.tab.employee": "Employee",
  "settings.col.setting": "Settings",
  "settings.default": "Default value",
  "settings.setHere": "Set for this person",
  "settings.clear": "Clear",
  "settings.none": "No setting matches.",
  "screen.actions": "Actions",
  "actions.title": "Actions",
  "actions.col.action": "Action",
  "actions.effect.read": "Read",
  "actions.effect.write": "Write",
  "actions.alsoGates": "+{count} more",
  "actions.col.area": "Area",
  "actions.col.effect": "Effect",
  "actions.col.audited": "Audited",
  "actions.col.needs": "Needs",
  "actions.needs.nothing": "Nothing",
  "actions.yes": "Yes",
  "actions.no": "No",
  "actions.surface.system": "System",
  "actions.search": "Search an action",
  "actions.none": "No action matches.",
  "screen.permissions": "Permissions",
  "permissions.title": "Permissions",
  "permissions.tab.role": "Role",
  "permissions.tab.employee": "Employee",
  "permissions.col.limit": "Limit",
  "permissions.col.expires": "Expires",
  "permissions.source.staffRole": "By role",
  "permissions.source.staffMember": "Override",
  "permissions.source.allStaff": "All staff",
  "permissions.source.default": "None",
  "permissions.noLimit": "No ceiling",
  "permissions.featureOff": "Feature is off",
  "permissions.approvable": "Can ask",
  "permissions.pickRole": "Pick a role",
  "audit.kind.featureEnablements": "Feature Enablements",
  "audit.kind.permissions": "Permissions",
  "audit.kind.teams": "Roles & teams",
  "audit.kind.actions": "Actions",
  "audit.kind.userSettings": "User Settings",
  "screen.roles": "Roles",
  "roles.title": "Roles",
  "roles.grants.none": "Grants nothing yet",
  "roles.grants.permissions": "Change its permissions",
  "roles.grants.features": "Change its features",
  "roles.explain":
    "What somebody is trained and trusted to do. A role is the same on every team, so leading one is not a role — that is set on Teams.",
  "roles.col.name": "Role",
  "roles.col.description": "What it covers",
  "roles.col.members": "People",
  "roles.none": "No roles yet.",
  "roles.loading": "Loading the roles…",
  "roles.loadFailed": "The roles could not be read.",
  "roles.retry": "Try again",
  "roles.edit": "Rename",
  "roles.save": "Save",
  "roles.cancel": "Cancel",
  "roles.created": "Role created.",
  "roles.new": "New role",
  "roles.new.title": "New role",
  "roles.back": "← All roles",
  "roles.opens": "What it opens",
  "roles.opens.explain":
    "The screens this role can reach, from the ticks on the left. Not a separate list — a screen appears because the role holds what it needs, and the API checks the same thing.",
  "roles.new.name": "Name",
  "roles.new.description": "What it covers",
  "roles.new.create": "Create the role",
  "roles.new.nameNeeded": "Give the role a name.",
  "roles.new.grantsNeeded": "Pick what the role is for: a role that grants nothing has no effect.",
  "roles.new.grants": "What it can use",
  "roles.new.permissions": "What it may do",
  "roles.lock.critical": "Only an Owner can grant this.",
  "roles.lock.high": "You can only grant what you hold yourself.",
  "roles.lock.readOnly": "You cannot grant on a role.",
  "roles.lock.featureFirst": "Switch on what it sits under first.",
  "screen.teams": "Teams",
  "teams.title": "Teams",
  "teams.col.name": "Team",
  "teams.col.purpose": "What it does",
  "teams.col.lead": "Led by",
  "teams.col.members": "People",
  "teams.col.status": "Status",
  "teams.col.person": "Person",
  "teams.col.roles": "Role",
  "teams.col.onTeam": "On the team",
  "teams.col.leads": "Leads it",
  "teams.open": "Open",
  "teams.back": "All teams",
  "teams.none": "No teams yet.",
  "teams.noMembers": "Nobody is on this team yet.",
  "teams.noOthers": "Everybody is already on this team.",
  "teams.loading": "Loading the teams…",
  "teams.loadFailed": "The teams could not be read.",
  "teams.retry": "Try again",
  "teams.status.open": "Open",
  "teams.status.closed": "Closed",
  "teams.rename": "Rename",
  "teams.close": "Close the team",
  "teams.reopen": "Reopen it",
  "teams.closedReason": "Closed",
  "teams.reopenedReason": "Reopened",
  "teams.closedNote": "Closed. Everybody on it is kept: reopening it finds them still here.",
  "teams.new": "New team",
  "teams.explain":
    "A group of people who do the same kind of work, with a lead. Work goes to the team, not to a person: whoever is on today picks up the next one. A team grants nothing.",
  "teams.new.people":
    "Who is on it from the moment it exists. Tick “Leads it” to say whose people these are: they reassign the team’s work and stand in for anyone on it.",
  "teams.new.name": "Name",
  "teams.new.purpose": "What it does",
  "teams.new.create": "Create the team",
  "teams.new.cancel": "Cancel",
  "teams.new.nameNeeded": "Give the team a name.",
  "teams.nameTaken": "A team with that name already exists.",
  "teams.created": "Team created.",
  "teams.leadHint": "Whose people these are: they reassign the team's work and stand in for anyone on it.",
  "teams.leadNeedsMember": "Somebody can only lead a team they are on.",
  "teams.noLead": "No lead",
  "teams.gone": "Somebody who has left cannot be put on a team.",
  "teams.locked": "You cannot change teams.",
  "screen.rules": "Rules",
  "screen.hr": "Human Resources",
  "sub.hr": "Look anybody up. Recruiting, records and everything under them are Human Resources'.",
  "screen.team": "Roles & team",
  "screen.audit": "Walliyo Audits",

  // ── Locked screen (opened by URL without the feature) ──
  "lock.requires": "This screen requires",
  "lock.or": " or ",
  "lock.roles": "Your roles ({roles}) don't include it, so it doesn't appear in your menu.",
  "lock.noRoles": "You have no roles, so it doesn't appear in your menu.",

  // ── Sign-in ──
  "signIn.title": "Sign in to the console",
  "signIn.subtitle": "For the Walliyo team only. We'll send you a code by SMS.",
  "signIn.email": "Work email",
  "signIn.password": "Password",
  "signIn.submit": "Continue",
  "signIn.submitting": "Checking…",
  "verify.title": "Verification code",
  "verify.subtitle": "We sent a code by SMS to {phone}.",
  "verify.code": "Code from the SMS",
  "verify.submit": "Sign in",
  "verify.submitting": "Checking…",
  "verify.expiresIn": "The code expires in {time}.",
  "verify.expired": "The code has expired. Sign in again to get a new one.",
  "verify.restart": "Sign in again",

  // ── Errors: what failed and what to do (ADR-0016 §2.5) ──
  "error.requestId": "Request ID: {id}",
  "error.noRequestId": "No request ID: the server didn't answer.",
  "error.signIn.unauthenticated": "Incorrect email or password. Check them and try again.",
  "error.signIn.validation_error": "Enter a valid email and password.",
  "error.verify.validation_error": "Enter the 6-digit code from the SMS.",
  "error.verify.session_expired": "The code has expired. Sign in again to get a new one.",
  "error.verify.unauthenticated": "This code request is no longer valid. Sign in again.",
  "error.unauthenticated": "You're no longer signed in. Sign in again.",
  "error.session_expired": "Your session has expired. Sign in again.",
  "error.feature_not_enabled": "Your roles don't allow this action. Ask a manager for access.",
  "error.invalid_code": "Incorrect code. Check the SMS and enter the code again.",
  "error.too_many_attempts": "Too many attempts. Wait a few minutes, then sign in again.",
  "error.stale_version": "This changed while you were looking at it. Reload to see the current version.",
  "error.validation_error": "Some information is invalid. Correct it and try again.",
  "error.internal_error":
    "The server ran into an error. Try again; if it keeps happening, send the ID below to the tech team.",
  "error.network_error": "No answer from the server after several attempts. Check your connection, then try again.",
  "error.network_error.write":
    "No answer from the server: the request may have gone through. Try again; it won't be done twice.",
  "error.unexpected_response":
    "Unexpected answer from the server. Try again; if it keeps happening, send the ID below to the tech team.",
  "error.unknown":
    "Something unexpected went wrong in the console. Reload the page; if it keeps happening, tell the tech team.",

  "sub.cases": "Shared queue · calls, emails and chat in one place",

  "sub.customers": "Search for a customer or a pro and see their whole account",

  "sub.verifications": "Check pros' identity before they can be found",

  "sub.reports": "Reports about pros or customers, by severity",

  "sub.billing": "Prices by country, promos and discounts · a pro keeps the price they subscribed at",
  "sub.payments": "Pro fees · Orange Money and card",

  "sub.features": "Turn on or off by level: everyone, a region, a version, one person",

  "sub.rules": "Company values · every change creates a version",

  // ── Fonctionnalités ──
  "features.title": "Feature Enablements",
  "features.loading": "Loading features…",
  "features.loadFailed": "Couldn't load the features.",
  "features.retry": "Try again",
  "features.close": "Close",
  "features.pick": "Choose a feature to see its exceptions.",
  "features.notFound": "This feature doesn't exist, or no longer does. Choose one from the list.",
  "features.subject.for": "Record",
  "features.subject.allClients": "All clients",
  "features.subject.allPros": "All pros",
  "features.subject.user": "User",
  "features.subject.uuid": "UUID",
  "features.subject.uuidHint": "31290388",
  "features.subject.unknown": "UUID not found.",
  "features.kind.client": "Client",
  "features.kind.pro": "Pro",
  "features.col.source": "Source",
  "features.col.value": "Value",
  "features.col.category": "Category",
  "features.value.on": "On",
  "features.value.off": "Off",
  "features.source.default": "Default",
  "features.source.thisUser": "This user",
  "features.source.user": "One user",
  "features.source.allClients": "All clients",
  "features.source.allPros": "All pros",
  "features.source.proProfile": "Pro profile",
  "features.source.region": "Region",
  "features.source.appVersion": "App version",
  "features.source.system": "System",
  "features.locked.owner": "Only an Owner can change it",
  "features.locked.scope": "Not settable at this level",
  "features.save.discard": "Discard",
  "features.save.sending": "Saving…",
  "features.save.nothing": "Nothing to save: no change was made.",
  "features.save.locked": "One of the changes is not yours to make.",
  "features.save.reasonNeeded": "Say why, in a few words.",
  "features.save.leave": "You have unsaved changes.",
  "features.clear": "Go back to the inherited value",
  "features.cleared": "Will be cleared",
  "features.filter.any": "Any",
  "features.filter.own": "Set here",
  "features.filter.inherited": "Inherited",
  "features.filter.search": "Search",
  "features.filter.none": "No feature matches these filters.",
  "features.surface.app": "Walliyo app",
  "features.surface.console": "Walliyo console",
  "features.save.title.one": "1 unsaved change",
  "features.save.title.many": "{count} unsaved changes",
  "features.save.submit.one": "Save 1 change",
  "features.save.submit.many": "Save {count} changes",
  "features.save.done.one": "1 change saved.",
  "features.save.done.many": "{count} changes saved.",
  "features.unsaved": "unsaved",
  "features.surface.consoleSoon":
    "The {count} console features can still only be set by role. Setting them per person lands here.",
  "features.surface.consoleWhere": "Open Roles & team",
  "features.subject.isStaff":
    "That number belongs to a Walliyo employee. Their features are on the Walliyo console tab.",
  "features.subject.search": "Search",
  "features.subject.searching": "Searching…",
  "features.subject.needsUuid": "Type the eight digits of the UUID.",
  "features.subject.pickFirst": "Choose who, then search.",
  "features.subject.stale": "These results are from your last search. Search again to update them.",
  "features.pill.allClients": "All client records",
  "features.pill.allPros": "All pro records",
  "features.pill.unknown": "Unknown user",
  "features.pill.staff": "Walliyo employee",
  "features.subject.allStaff": "All staff",
  "features.subject.staffMember": "Employee",
  "features.staffSource.override": "Override",
  "features.staffSource.byRole": "By role",
  "features.staffSource.byRoleNamed": "By role: {roles}",
  "features.staffSource.allStaff": "All staff",
  "features.locked.ownAccount": "You can't change your own account",
  "features.locked.grant": "You can't grant this one",
  "features.pill.allStaff": "All staff records",
  "features.pill.unknownStaff": "Unknown employee",
  "features.subject.isCustomer":
    "That number belongs to a customer, not an employee. Their features are on the Walliyo app tab.",
  "features.subject.employeeUuid": "Employee UUID",
  "features.subject.staffUuidHint": "27217102",
  "features.col.feature": "Feature",
  "features.col.default": "Default",
  "features.col.exceptions": "Exceptions",
  "features.for.clients": "For customers",
  "features.for.pros": "For pros",
  "features.for.everyone": "For everyone",
  "features.on": "On",
  "features.off": "Off",
  "features.planned": "Planned",
  "features.planned.note": "Planned: a value can be set, but no code reads it yet.",
  "features.detail.default": "Default: {value}",
  "features.sensitivity.open": "{level} sensitivity: anyone who manages app features can change it.",
  "features.sensitivity.critical": "Critical sensitivity: only an owner can change it, and the owners are notified.",
  "features.locked.critical":
    "Locked: this feature is critical and only an owner can change it. Your roles ({roles}) don't make you an owner; the server would refuse too.",
  "features.exceptions.title": "Exceptions by level",
  "features.col.scope": "Level",
  "features.col.target": "Target",
  "features.col.reason": "Reason",
  "features.col.expires": "Expires",
  "features.col.actions": "Manage",
  "features.default.scope": "Default",
  "features.default.reason": "default value",
  "features.exceptions.none": "No exceptions: the default applies to everyone.",
  "features.expired": "Expired",
  "features.expires.never": "Until changed",
  "features.edit": "Edit",
  "features.remove": "Remove",
  "features.add": "Add an exception",
  "features.scope.system": "System",
  "features.scope.allClients": "All customers",
  "features.scope.allPros": "All pros",
  "features.scope.region": "Region",
  "features.scope.appVersion": "App version",
  "features.scope.user": "One person",
  "features.scope.proProfile": "One pro profile",
  "features.scope.clientProfile": "One client profile",
  "features.target.everyone": "Everyone",
  "features.target.allClients": "All customers",
  "features.target.allPros": "All pros",
  "features.target.appVersion": "Version {version} and newer",
  "features.target.unnamed": "Account without a name",
  "features.form.addTitle": "New exception",
  "features.form.editTitle": "Edit the exception",
  "features.form.scope": "Level",
  "features.form.scopePick": "Choose a level…",
  "features.form.scopeUnsupported": "{scope} (not possible yet)",
  "features.form.clientProfileNote":
    "A client profile can't hold a value yet: client profiles don't exist yet. For one customer, choose “One person”.",
  "features.form.needsSearch": "Choosing a person or pro profile needs",
  "features.form.needsView": "Finding an account's pro profile needs",
  "features.form.region": "Region",
  "features.form.regionPick": "Choose a region…",
  "features.form.appVersion": "From version",
  "features.form.appVersionHint": "For example 2.4.0: applies to that version and newer ones.",
  "features.form.person": "Person",
  "features.form.pro": "Pro profile",
  "features.form.search": "Search",
  "features.form.searching": "Searching…",
  "features.form.searchPlaceholder": "Name, phone, email or account ID",
  "features.form.noResults": "No account matches.",
  "features.form.pickedChange": "Change",
  "features.form.value": "Value",
  "features.form.reason": "Reason (required)",
  "features.form.reasonHint": "Recorded in the audit log with the change.",
  "features.form.expires": "Expires on",
  "features.form.expiresHint": "Optional. Empty: until changed.",
  "features.form.version": "Based on version {version} of this exception.",
  "features.form.submitAdd": "Add the exception",
  "features.form.submitEdit": "Save",
  "features.form.cancel": "Cancel",
  "features.form.sending": "Sending…",
  "features.problem.scope": "Choose one of the levels offered.",
  "features.problem.region": "Choose a region.",
  "features.problem.appVersion": "Enter a version like 2.4.0.",
  "features.problem.person": "Search for the person and choose them from the results.",
  "features.problem.pro": "Search for the pro and choose an account with a pro profile.",
  "features.problem.exists": "There is already an exception for this target: edit it instead.",
  "features.problem.reason": "Give a reason in a few words.",
  "features.problem.expiryInvalid": "This expiry date isn't valid.",
  "features.problem.expiryPast": "The expiry must be in the future, or leave it empty.",
  "features.refused.critical": "Refused: this feature is critical, only an owner can change it.",
  "features.refused.scopeNotSupported":
    "Refused: a client profile can't hold a value yet, client profiles don't exist yet. Choose “One person”.",
  "features.refused.scopeNotAllowed":
    "Refused: this feature can't be set at that level. Choose one of the levels offered.",
  "features.refused.expiryInPast": "Refused: the expiry is in the past. Choose a later date, or leave it empty.",
  "features.refused.targetNotFound": "Refused: that account or pro profile no longer exists. Search for it again.",
  "features.stale": "Someone changed this feature meanwhile. Reload to see its current exceptions, then try again.",
  "features.done.added": "Exception added.",
  "features.done.saved": "Exception saved.",
  "features.done.removed": "Exception removed: the broader level, or the default, applies again.",
  "features.remove.title": "Remove the exception",
  "features.remove.body":
    "Remove the exception “{scope} · {target}: {value}” from {feature}. The broader level, or the default, will apply again.",
  "features.remove.submit": "Remove",

  // ── Règles ──
  "rules.loading": "Loading rules…",
  "rules.loadFailed": "Couldn't load the rules.",
  "rules.retry": "Try again",
  "rules.pick": "Choose a rule to see its history.",
  "rules.notFound": "This rule doesn't exist. Choose one from the list.",
  "rules.next": "from {date}: {value}",
  "rules.now": "In force",
  "rules.default": "default value",
  "rules.kind.gnf": "amount in GNF",
  "rules.kind.minutes": "duration in minutes",
  "rules.kind.hours": "duration in hours",
  "rules.kind.days": "duration in days",
  "rules.kind.km": "distance in km",
  "rules.kind.number": "number",
  "rules.kind.boolean": "yes or no",
  "rules.kind.choice": "choice from a list",
  "rules.kind.text": "text",
  "rules.sensitivity.open": "{level} sensitivity: anyone who can edit rules can add a version.",
  "rules.sensitivity.critical": "Critical sensitivity: only an owner can add a version.",
  "rules.legalBasis.required": "Every change must cite its legal basis.",
  "rules.locked.critical":
    "Locked: this rule is critical, only an owner can add a version. Your roles ({roles}) don't make you an owner; the server would refuse too.",
  "rules.locked.retired": "This rule is retired: nothing reads it any more, so it can't change.",
  "rules.history": "Version history",
  "rules.history.none": "No versions: the default ({value}) applies.",
  "rules.col.version": "Version no.",
  "rules.col.value": "Value",
  "rules.col.effective": "In force",
  "rules.col.reason": "Reason",
  "rules.col.legalBasis": "Legal basis",
  "rules.col.by": "By",
  "rules.status.scheduled": "From {date}",
  "rules.status.current": "Current",
  "rules.status.since": "since {date}",
  "rules.status.past": "Replaced",
  "rules.new": "New version",
  "rules.form.title": "New version",
  "rules.form.value": "New value",
  "rules.form.value.gnf": "New amount (GNF)",
  "rules.form.value.minutes": "New value (in minutes)",
  "rules.form.value.hours": "New value (in hours)",
  "rules.form.value.days": "New value (in days)",
  "rules.form.effective": "Takes effect",
  "rules.form.now": "Now",
  "rules.form.at": "At a date and time",
  "rules.form.atLabel": "In force from",
  "rules.form.reason": "Reason (required)",
  "rules.form.legalBasis": "Legal basis (required)",
  "rules.form.legalBasisOptional": "Legal basis (if any)",
  "rules.form.legalBasisPlaceholder": "Law, contract, legal advice",
  "rules.form.version":
    "Based on version {version} of the rule: if another version is added meanwhile, this will be refused.",
  "rules.form.submit": "Create the version",
  "rules.form.cancel": "Cancel",
  "rules.form.sending": "Sending…",
  "rules.value.true": "Yes",
  "rules.value.false": "No",
  "rules.problem.value": "Enter a value.",
  "rules.problem.number": "Enter a number, for example 750000.",
  "rules.problem.integer": "Enter a whole number.",
  "rules.problem.min": "The value must be at least {min}.",
  "rules.problem.max": "The value must be at most {max}.",
  "rules.problem.choice": "Choose a value from the list.",
  "rules.problem.tooLong": "{max} characters at most.",
  "rules.problem.effectiveMissing": "Choose the date and time it takes effect.",
  "rules.problem.effectivePast":
    "A version can't take effect in the past: what applied then stays on record. Choose now or later.",
  "rules.problem.reason": "Give a reason in a few words.",
  "rules.problem.legalBasis": "Cite the law, contract or legal advice behind this change.",
  "rules.refused.critical": "Refused: this rule is critical, only an owner can add a version.",
  "rules.refused.effectivePast": "Refused: that date is in the past. Choose now or later.",
  "rules.refused.legalBasis": "Refused: this rule needs a legal basis. Cite the law, contract or legal advice.",
  "rules.refused.invalidValue": "Refused: that value doesn't fit the rule. Check the unit and the minimum.",
  "rules.refused.retired": "Refused: this rule is retired, nothing reads it any more.",
  "rules.stale": "Another version of this rule was added meanwhile. Reload to see it, then try again.",
  "rules.done.now": "Version {version} created: it applies now.",
  "rules.done.scheduled": "Version {version} created: it applies from {date}.",

  "sub.team": "Each role is a list of features. Nobody can grant anything to themselves.",

  "sub.audit": "Everything that was done, by whom, for whom · your viewing is itself recorded",

  // ── Audit log ──
  "audit.filter.subject": "Person concerned",
  "audit.filter.subjectPlaceholder": "Account ID",
  "audit.filter.staff": "Team member",
  "audit.filter.staffPlaceholder": "Member ID",
  "audit.filter.action": "Action",
  "audit.filter.area": "Area",
  "audit.filter.outcome": "Outcome",
  "audit.filter.from": "From",
  "audit.filter.to": "To (inclusive)",
  "audit.filter.all": "All",
  "audit.filter.allActions": "All",
  "audit.filter.clear": "Clear filters",
  "audit.problem.id": "An ID looks like 3f2a…-…-…-…-… (UUID). Copy it from the account page.",
  "audit.problem.period": "The end of the period is before its start.",
  "audit.problem.needsPerson": "Give the updater's UUID, the person's, or both.",
  "audit.filtersAria": "Log filters",
  "audit.searchAria": "Log search",
  "audit.scope.user": "Individual",
  "audit.scope.allStaff": "All staff",
  "audit.scope.allClients": "All clients",
  "audit.scope.allPros": "All pros",
  "audit.scope.staffRole": "Role",
  "audit.scope.proProfile": "Pro profile",
  "audit.scope.region": "Region",
  "audit.scope.appVersion": "App version",
  "audit.scope.system": "System",
  "audit.scope.staffMember": "Individual",
  "audit.filter.involving": "Involving",
  "audit.filter.updaterUuid": "Updater UUID",
  "audit.filter.userUuid": "User UUID",
  "audit.pill.updater": "Updater",
  "audit.pill.user": "Person",
  "audit.col.time": "Time",
  "audit.col.actor": "Updater",
  "audit.col.action": "Setting",
  "audit.col.subject": "Concerns",
  "audit.col.outcome": "Outcome",
  "audit.col.reason": "Reason",
  "audit.filter.effect": "Show",
  "audit.effect.write": "Changes",
  "audit.effect.read": "Reads",
  "audit.col.before": "Before",
  "audit.col.after": "After",
  "audit.change.arrow": "{before} → {after}",
  "audit.change.field": "{key}: {value}",
  "audit.outcome.succeeded": "Succeeded",
  "audit.outcome.denied": "Denied",
  "audit.outcome.requested": "Requested",
  "audit.actor.staff": "Team member",
  "audit.actor.noRoles": "no roles",
  "audit.actor.customer": "customer",
  "audit.actor.self": "customer (themselves)",
  "audit.actor.system": "System",
  "audit.subject.onBehalf": "on behalf of {id}",
  "audit.filterBy": "Filter on {id}",
  "audit.subject.open": "Open {name}'s account",
  "audit.masked": "masked",
  "audit.sensitive": "🔒 sensitive",
  "audit.loading": "Loading the log…",
  "audit.loadFailed": "The log couldn't be loaded.",
  "audit.retry": "Try again",
  "audit.empty": "No entries match these filters.",
  "audit.emptyForPerson": "No entry for this search.",
  "audit.fixProblems": "Correct the flagged filters to show the log.",
  "audit.loadMore": "Load more",
  "audit.loadingMore": "Loading…",
  "audit.moreFailed": "The next page couldn't be loaded. The entries already shown are still valid.",
  "audit.count": "{count} entries shown",
  "audit.countOne": "1 entry shown",
  "audit.end": "End of the log for these filters.",
  "audit.recorded": "Your viewing is itself recorded (VIEW_AUDIT_LOG_STAFF_ACTION), with the filters used.",
  "audit.sensitiveShown": "You can see sensitive values (VIEW_SENSITIVE_AUDIT_VALUES).",
  "audit.sensitiveHidden":
    "Sensitive values are masked: they require VIEW_SENSITIVE_AUDIT_VALUES, granted by the owner only.",
  "audit.deniedNote": "A “Denied” row shows that an attempt without permission is recorded too.",
  "area.account": "Account",
  "area.access": "Access",
  "area.onboarding": "Pro sign-up",
  "area.discovery": "Search",
  "area.jobs": "Requests",
  "area.leads": "Leads",
  "area.messaging": "Messages",
  "area.reviews": "Reviews",
  "area.payments": "Payments",
  "area.support": "Cases",
  "area.safety": "Safety",
  "area.settings": "Settings",
  "area.staff": "Team",
  "area.audit": "Audit",

  // ── Roles & team ──
  "team.members": "Team",
  "team.col.name": "Name",
  "team.col.roles": "Roles",
  "team.col.status": "Status",
  "team.col.lastSeen": "Last sign-in",
  "team.you": "you",
  "team.noRoles": "No roles",
  "team.status.active": "Active",
  "team.status.invited": "Invited",
  "team.status.deactivated": "Deactivated",
  "team.sessions": "{count} open sessions",
  "team.sessionsOne": "1 open session",
  "team.lastSeen.never": "Never signed in",
  "team.lastSeen.today": "today at {time}",
  "team.lastSeen.yesterday": "yesterday at {time}",
  "team.lastSeen.date": "on {date}",
  "team.invite": "Invite",
  "team.manageRoles": "Roles",
  "team.endSessions": "End session",
  "team.deactivate": "Deactivate",
  "team.loading": "Loading the team…",
  "team.loadFailed": "The team couldn't be loaded.",
  "team.retry": "Try again",
  "team.emptyStaff": "Nobody on the team yet.",
  "team.grid": "Roles × features",
  "team.grid.feature": "Feature",
  "team.grid.editable": "Click to grant or revoke · “critical” requires the owner",
  "team.grid.readOnly": "🔒 Read only · editing requires MANAGE_ROLES or ASSIGN_ROLES",
  "team.grid.members": "{count} members",
  "team.grid.membersOne": "1 member",
  "team.grid.yourRole": "🔒 your role",
  "team.grid.newRole": "New role",
  "team.grid.editRole": "Edit",
  "team.grid.noRoles": "No roles exist yet.",
  "team.lock.ownRole": "You hold this role: nobody changes what their own role grants.",
  "team.lock.critical": "Critical: only the owner can grant or revoke it.",
  "team.lock.high": "High sensitivity: requires MANAGE_ROLES and holding this feature yourself.",
  "team.lock.low": "Requires MANAGE_ROLES or ASSIGN_ROLES.",
  "team.lock.criticalShort": "🔒 owner",
  "team.lock.highShort": "🔒 beyond your permissions",
  "team.cell.grant": "Grant {feature} to the {role} role",
  "team.cell.revoke": "Revoke {feature} from the {role} role",
  "team.sensitivity.low": "low",
  "team.sensitivity.high": "high",
  "team.sensitivity.critical": "critical",

  "team.reason": "Reason",
  "team.reasonHint": "Recorded in the audit log. A few words are enough.",
  "team.reasonTooShort": "Give a reason of at least 3 characters.",
  "team.cancel": "Cancel",
  "team.close": "Close",
  "team.sending": "Sending…",
  "team.reload": "Reload",
  "team.refused": "Refused: {message}",
  "team.stale": "Someone changed this in the meantime. Reload to see the current version, then try again.",

  "team.grant.title": "Grant a feature",
  "team.revoke.title": "Revoke a feature",
  "team.grant.body": "Grant {feature} to the {role} role ({members}).",
  "team.revoke.body": "Revoke {feature} from the {role} role ({members}).",
  "team.grant.submit": "Grant",
  "team.revoke.submit": "Revoke",
  "team.grant.done": "{feature} granted to the {role} role.",
  "team.revoke.done": "{feature} revoked from the {role} role.",
  "team.critical.note": "Critical feature: the owners will be notified.",

  "team.invite.title": "Invite a team member",
  "team.invite.name": "Full name",
  "team.invite.email": "Work email",
  "team.invite.phone": "Phone",
  "team.invite.phoneHint": "International format, e.g. +224621234567.",
  "team.invite.roles": "Roles",
  "team.invite.noRoles": "Create a role first — you can invite them now and give them roles afterwards.",
  "team.invite.rolesHint": "Without a role they sign in to a console with nothing open to them.",
  "team.invite.sms":
    "A 6-digit code will be sent by SMS to this number. Valid for {hours} hours, it lets them choose a password on the “Choose my password” page.",
  "team.invite.submit": "Send the invitation",
  "team.invite.done": "{name} is invited. Code sent by SMS, valid until {until}.",
  "team.invite.missing": "Enter the name, email and phone.",

  "team.deactivate.title": "Deactivate {name}",
  "team.deactivate.body":
    "{name} will never be able to sign in again. Their open sessions and any pending invitation end immediately. A deactivated member isn't deleted.",
  "team.deactivate.submit": "Deactivate",
  "team.deactivate.done": "{name} is deactivated. Sessions ended: {count}.",
  "team.reactivate": "Reactivate",
  "team.reactivate.title": "Reactivate {name}",
  "team.reactivate.body":
    "{name} will be able to sign in again, with the roles they already had. They sign in from scratch — no session or code is restored.",
  "team.reactivate.submit": "Reactivate",
  "team.reactivate.done": "{name} is active again.",

  "team.endSessions.title": "End {name}'s sessions",
  "team.endSessions.body": "{name} will be signed out everywhere ({sessions}).",
  "team.endSessions.self": "These are your own sessions: you'll be signed out too.",
  "team.endSessions.submit": "End",
  "team.endSessions.done": "{name}'s sessions ended: {count}.",

  "team.roles.title": "{name}'s roles",
  "team.roles.held": "Current roles",
  "team.roles.available": "Other roles",
  "team.roles.none": "None",
  "team.roles.assign": "Assign {role}",
  "team.roles.remove": "Remove {role}",
  "team.roles.assignDone": "{role} assigned to {name}.",
  "team.roles.removeDone": "{role} removed from {name}.",
  "team.roles.reasonFirst": "Give a reason first, then choose the role to assign or remove.",

  "team.role.createTitle": "New role",
  "team.role.editTitle": "Edit the {name} role",
  "team.role.name": "Role name",
  "team.role.description": "Description",
  "team.role.descriptionHint": "Optional. What this role does, in one sentence.",
  "team.role.department": "Department",
  "team.role.noDepartment": "None",
  "team.role.departmentHint":
    "The workspace its holders land in. What the role may do is still decided by its features.",
  "team.role.create": "Create the role",
  "team.role.save": "Save",
  "team.role.created": "Role {name} created. Grant it features in the grid.",
  "team.role.saved": "Role {name} saved.",
  "team.role.nameTooShort": "The name must be at least 2 characters.",
  "team.role.unchanged": "Change the name or the description.",
  "team.role.version": "Version {version}",

  // ── Permissions on a role (ADR-0019) and who can do this ──
  "team.tab.roles": "Roles & team",
  "team.tab.who": "Who can do this",
  "team.tabs.aria": "Team sections",
  "team.permission.noLimit": "No limit",
  "team.permission.lockFeature": "Grant {feature} to {role} first: a permission counts only under its feature.",
  "team.cell.grantPermission": "Grant {permission} to the role {role}",
  "team.cell.editPermission": "Change {permission} for the role {role}",
  "team.permission.grantTitle": "Grant a permission",
  "team.permission.editTitle": "Change a permission",
  "team.permission.grantBody": "Grant {permission} to the role {role} ({members}).",
  "team.permission.editBody": "The role {role} ({members}) holds {permission}.",
  "team.permission.under": "Under {feature}.",
  "team.permission.approvable": "Without it, their attempt becomes an approval request.",
  "team.permission.refusable": "Without it, the action is refused.",
  "team.permission.limits": "Limits",
  "team.permission.limitsHint":
    "The most its holders may do alone in each currency. Above it, or in a currency with no limit, it becomes an approval request.",
  "team.permission.noLimitHint": "No limit: its holders act alone, whatever the amount.",
  "team.permission.currency": "Currency",
  "team.permission.amount": "Amount",
  "team.permission.addLimit": "Add a limit in another currency",
  "team.permission.removeLimit": "Remove the limit in {currency}",
  "team.permission.limitProblem": "Give a three-letter currency and an amount above zero for every limit.",
  "team.permission.duplicateCurrency": "One limit per currency: {currency} is named twice.",
  "team.permission.grantDone": "{permission} granted to the role {role}.",
  "team.permission.revokeDone": "{permission} revoked from the role {role}.",
  "team.revoke.permissionsToo": "Its permissions go with it: {permissions}.",
  "team.refused.sensitivity": "Your access doesn't allow granting or revoking this. Ask an Ops Manager or the owner.",
  "team.refused.critical": "Critical: only an owner who holds it grants or revokes it.",
  "team.refused.limitAboveOwn": "You can't grant a limit higher than your own. Ask the owner.",
  "team.refused.featureFirst": "This permission sits on a feature the role doesn't grant. Grant the feature first.",
  "team.refused.notLimited": "This permission carries no limit.",
  "team.refused.limitWithoutGrant": "A limit goes with a granted permission.",
  "team.refused.ownRole": "You hold this role, so you can't change what it grants. Ask another owner.",
  "team.who.action": "Action",
  "team.who.feature": "Feature",
  "team.who.permission": "Permission",
  "team.who.noPermission": "No permission covers this action: whoever holds the feature may take it.",
  "team.who.approvable": "can be asked for",
  "team.who.col.through": "Through",
  "team.who.col.limit": "Limit",
  "team.who.personal": "Set for them personally",
  "team.who.mayRequest": "May only ask: becomes an approval request",
  "team.who.loading": "Loading who can do this…",
  "team.who.loadFailed": "Couldn't load who can do this.",
  "team.who.empty": "Nobody can do this.",

  // ── Customers & pros ──
  // Trades, by WorkerCategory.
  "trade.electrician": "Electrician",
  "trade.plumber": "Plumber",
  "trade.mason": "Mason",
  "trade.carpenter": "Carpenter",
  "trade.ac_technician": "AC technician",
  "trade.cleaner": "Cleaner",
  "trade.painter": "Painter",
  "trade.handyman": "Handyman",
  "trade.laundry": "Laundry",
  "trade.babysitter": "Babysitter",
  "trade.fridge_repair": "Fridge repairer",
  "trade.moto_driver": "Moto-taxi",
  "trade.tailor": "Tailor",
  "trade.car_mechanic": "Car mechanic",
  "trade.welder": "Welder",
  "trade.gardener": "Gardener",
  "trade.phone_repair": "Phone repairer",
  "trade.tutor": "Tutor",
  "trade.hairdresser": "Hairdresser",
  "trade.security_guard": "Security guard",
  "trade.other": "Other",
  // A number with its unit (the settings' Unit).
  "unit.km": "{value} km",
  "unit.minutes": "{value} min",
  "unit.hours": "{value} h",
  "unit.days": "{value} d",
  "unit.seconds": "{value} s",

  "customers.search.label": "Search for a customer or a pro",
  "customers.search.placeholder": "Name, phone, email or account ID",
  "customers.search.hint": "E.g. “621 23 45 67”, “Sékou Touré”, “mariama.bah@” or the account ID.",
  "customers.search.submit": "Search",
  "customers.search.tooShort": "Enter at least 2 characters.",
  "customers.search.locked": "Search requires",
  "customers.search.idle": "Enter a name, number, email or ID to find an account.",
  "customers.search.loading": "Searching…",
  "customers.search.loadFailed": "The search couldn't be completed.",
  "customers.search.empty": "No account matches “{query}”.",
  "customers.search.matchedBy.userId": "Searched by account ID",
  "customers.search.matchedBy.email": "Searched by email",
  "customers.search.matchedBy.phone": "Searched by phone",
  "customers.search.matchedByPhone": "Searched by phone: {phone}",
  "customers.search.matchedBy.name": "Searched by name",
  "customers.search.count": "{count} accounts shown",
  "customers.search.countOne": "1 account shown",
  "customers.search.end": "End of results.",
  "customers.search.loadMore": "Load more",
  "customers.search.loadingMore": "Loading…",
  "customers.search.moreFailed": "The next page couldn't be loaded. The accounts already shown are still valid.",
  "customers.search.retry": "Try again",
  "customers.search.masked":
    "Numbers and emails are masked. “Show”, on the account page and with a reason, is recorded.",
  "customers.col.name": "Name",
  "customers.col.contact": "Contact",
  "customers.col.kind": "Customer or pro",
  "customers.col.region": "Region",
  "customers.col.since": "Member since",
  "customers.deleted": "Account deleted",
  "customers.deletedName": "Name erased",
  "customers.kind.client": "Customer",
  "customers.kind.pro": "Pro",
  "customers.kind.proAndClient": "Pro and customer",
  "customers.kind.admin": "Administrator",
  "customers.region.gn": "Guinea",
  "customers.region.sn": "Senegal",
  "customers.region.us": "United States",
  "customers.region.eu": "Europe",
  "customers.verification.unverified": "Unverified",
  "customers.verification.pending": "Pending",
  "customers.verification.verified": "Verified",
  "customers.verification.rejected": "Rejected",

  "customers.back": "← Search",
  "customers.page.loading": "Loading the account…",
  "customers.page.loadFailed": "The account couldn't be loaded.",
  "customers.page.notFound": "No account has this ID. Search for the person again.",
  "customers.page.locked": "An account page requires",
  "customers.page.sub": "{kind} · member since {since} · {region}",
  "customers.page.deletedTitle": "Account deleted",
  "customers.page.deletedSub": "Deleted on {date} · name and contact details erased",

  "customers.reveal.button": "Show",
  "customers.reveal.locked": "🔒 masked",
  "customers.reveal.lockedTitle": "Showing contact details requires REVEAL_CONTACT_DETAILS_STAFF_FEATURE_ENABLEMENT",
  "customers.reveal.title": "Show {name}'s contact details",
  "customers.reveal.body":
    "The full number and email are shown on this page only, for as long as you're viewing it. Showing them is recorded in the account's log, with your reason.",
  "customers.reveal.reason": "Reason",
  "customers.reveal.placeholder": "Why? e.g. call the customer back, case D-0142",
  "customers.reveal.tooShort": "Give a reason of at least 3 characters.",
  "customers.reveal.submit": "Show",
  "customers.reveal.sending": "Sending…",
  "customers.reveal.cancel": "Cancel",
  "customers.reveal.noAnswer":
    "No answer from the server: the viewing may have been recorded without the details reaching you. Try again; a new viewing will be recorded too.",
  "customers.reveal.recorded": "Shown at {time} · viewing recorded in the account's log.",
  "customers.reveal.hide": "Hide",
  "customers.reveal.phone": "Phone:",
  "customers.reveal.email": "Email:",
  "customers.reveal.none": "none",

  "customers.obo.title": "Act on behalf of {name}",
  "customers.obo.body":
    "A code is sent by SMS to the account's number. The customer reads it aloud; the session lasts 30 minutes and every action is recorded with their name and yours.",
  "customers.obo.send": "Send the code",
  "customers.obo.soon": "Coming soon",

  "customers.tabs.aria": "Account sections",
  "customers.tab.overview": "Overview",
  "customers.tab.subscription": "Subscription",
  "customers.tab.settings": "Features & settings",
  "customers.tab.jobs": "Requests",
  "customers.tab.audit": "Log",

  "customers.pro.title": "Pro profile",
  "customers.pro.none": "No pro profile.",
  "customers.pro.identity": "Identity",
  "customers.pro.approval": "Approval",
  "customers.pro.rating": "Rating",
  "customers.pro.ratingValue": "{rating} ★ · {count} reviews",
  "customers.pro.noReviews": "No reviews",
  "customers.pro.rate": "Rate",
  "customers.pro.subscription": "Plan",
  "customers.pro.noSubscription": "None",
  "customers.pro.subscriptionLine": "{plan} · {status}",
  "customers.pro.paidUntil": "{plan} · paid until {date}",
  "customers.pro.endingUntil": "{plan} · active until {date}, not renewing",
  "customers.pro.approval.pending": "Pending",
  "customers.pro.approval.approved": "Approved",
  "customers.pro.approval.rejected": "Rejected",
  "customers.subscription.pending": "pending",
  "customers.subscription.active": "active",
  "customers.subscription.ending": "not renewing",
  "customers.subscription.suspended": "suspended",
  "customers.subscription.ended": "ended",
  "customers.rate.hourly": "{amount} / hour",
  "customers.rate.daily": "{amount} / day",
  "customers.rate.fixed": "{amount} flat rate",
  "customers.client.title": "Customer profile",
  "customers.client.jobsPosted": "Requests posted",
  "customers.client.lastActivity": "Last activity",
  "customers.client.never": "None",
  "customers.restrictions.title": "Active restrictions",
  "customers.restrictions.none": "No restrictions.",
  "customers.restrictions.suspension": "Temporary suspension",
  "customers.restrictions.line": "{feature} turned off for {scope} · reason: {reason}",
  "customers.restrictions.until": "until {date}",

  "customers.settings.col.setting": "Setting",
  "customers.settings.col.value": "Value",
  "customers.settings.col.source": "Level",
  "customers.settings.col.reason": "Reason",
  "customers.settings.col.expires": "Expires",
  "customers.settings.on": "On",
  "customers.settings.off": "Off",
  "customers.settings.restricted": "Restricted",
  "customers.settings.none": "No settings apply to this account.",
  "customers.settings.edit": "Edit a setting",
  "customers.settings.editSoon": "Coming soon: editing a customer's settings arrives in a future version.",
  "customers.settings.foot":
    "The most specific level wins: person, pro profile, all pros or all customers, region, system, then the default value.",
  "customers.source.user": "Person",
  "customers.source.proProfile": "Pro profile",
  "customers.source.allPros": "All pros",
  "customers.source.allClients": "All customers",
  "customers.source.region": "Region",
  "customers.source.regionOf": "Region {region}",
  "customers.source.appVersion": "App version",
  "customers.source.system": "System",
  "customers.source.default": "Default",
  "customers.scope.user": "the person",
  "customers.scope.proProfile": "the pro profile",

  "customers.jobs.col.trade": "Trade",
  "customers.jobs.col.type": "Type",
  "customers.jobs.col.side": "Role",
  "customers.jobs.col.other": "With",
  "customers.jobs.col.status": "Status",
  "customers.jobs.col.date": "Date",
  "customers.jobs.type.direct": "Direct",
  "customers.jobs.type.open": "Open",
  "customers.jobs.side.client": "Customer",
  "customers.jobs.side.pro": "Pro",
  "customers.jobs.status.requested": "Requested",
  "customers.jobs.status.quoted": "Quote sent",
  "customers.jobs.status.accepted": "Introduction made",
  "customers.jobs.status.cancelled": "Cancelled",
  "customers.jobs.noPro": "No pro chosen",
  "customers.jobs.deletedParty": "Account deleted",
  "customers.jobs.none": "No requests for this account.",
  "customers.jobs.foot": "The last {count} requests at most, as a customer or as a pro.",

  "customers.journal.recorded": "Your viewing of this log is itself recorded.",
  "customers.journal.locked": "The audit log requires",
  "customers.lockedRoles": "Your roles ({roles}) don't include it.",
  "customers.journal.onBehalf": "on behalf of {id}",
  "customers.journal.actorWithRoles": "{name} ({roles})",
  "customers.journal.reason": "reason: {reason}",
  "customers.journal.empty": "Nothing is recorded for this account yet.",
  "customers.journal.all": "Open in the audit log →",
  "customers.journal.denied": "denied",
  "customers.journal.succeeded": "succeeded",

  // ── Choose my password (public) ──
  "setPassword.title": "Choose my password",
  "setPassword.subtitle": "Use the 6-digit code you received by SMS with your invitation.",
  "setPassword.email": "Work email",
  "setPassword.code": "Code from the SMS",
  "setPassword.password": "New password",
  "setPassword.confirm": "Confirm the password",
  "setPassword.rules": "The password must:",
  "setPassword.rule.length": "be at least {min} characters",
  "setPassword.rule.tooLong": "be at most {max} characters",
  "setPassword.rule.mix": "contain a letter and a character that isn't a letter",
  "setPassword.rule.email": "not contain your email ({name})",
  "setPassword.rule.emailGeneric": "not contain your email",
  "setPassword.mismatch": "The two passwords don't match.",
  "setPassword.codeFormat": "The code is 6 digits.",
  "setPassword.emailFormat": "Enter a valid email.",
  "setPassword.submit": "Save my password",
  "setPassword.submitting": "Saving…",
  "setPassword.back": "Back to sign-in",
  "setPassword.done": "Password saved. Sign in with your email and this password.",
  "signIn.setPassword": "Invited? Choose my password",
  "signIn.or": "or",
  "signIn.heading": "Walliyo Console",
  "signIn.forgot": "Forgot password?",
  "signIn.environment": "Environment",
  "signIn.build": "Build",
  "signIn.googleUnavailable": "Sign in with Google isn't available. Use your email and password, or tell the Owner.",
  "error.setPassword.invalid_code":
    "This email and code don't match any open invitation. Check both; if the code is lost or expired, ask to be invited again.",
  "error.setPassword.too_many_attempts":
    "Too many incorrect codes: this code is no longer valid. Ask to be invited again.",
  "error.setPassword.validation_error":
    "The password doesn't follow the rules, or the code isn't in the right format. Correct it and try again.",

  // ── Dossiers (the support case queue) ──
  "cases.newCall": "+ Log a call",
  "cases.queue.aria": "Case queue",
  "cases.queue.views": "Queue views",
  "cases.view.all": "All",
  "cases.view.unassigned": "Unassigned",
  "cases.view.mine": "Mine",
  "cases.view.late": "Late",
  "cases.filter.status": "Status",
  "cases.filter.notClosed": "All but closed",
  "cases.filter.channel": "Channel",
  "cases.filter.anyChannel": "All channels",
  "cases.filter.customer": "Customer {id}",
  "cases.filter.clear": "Remove the customer filter",
  "cases.col.case": "Case",
  "cases.col.deadline": "Deadline",
  "cases.queue.loading": "Loading the queue…",
  "cases.queue.loadFailed": "The queue couldn't be loaded.",
  "cases.queue.empty": "No case matches these filters.",
  "cases.loadMore": "Load more",
  "cases.loadingMore": "Loading…",
  "cases.retry": "Try again",
  "cases.pick": "Pick a case in the queue to see its timeline.",
  "cases.loading": "Loading the case…",
  "cases.loadFailed": "This case couldn't be loaded.",
  "cases.notFound": "No case has this ID. It may have been copied wrong.",
  "cases.refreshFailed": "The case couldn't be refreshed: what you see may be out of date.",
  "cases.deadline.late": "Late · {time}",
  "cases.deadline.left": "{time} left",
  "cases.deadline.replied": "Replied",
  "cases.deadline.closed": "Closed",
  "cases.channel.phone": "Call",
  "cases.channel.email": "Email",
  "cases.channel.chat": "In-app chat",
  "cases.status.open": "Open",
  "cases.status.waiting_on_customer": "Waiting on customer",
  "cases.status.closed": "Closed",
  "access.mine.title": "What you have asked for",
  "access.mine.none": "You have not asked for anything.",
  "access.mine.what": "What",
  "access.mine.state": "Where it got to",
  "access.mine.asked": "Asked",
  "access.mine.withWhom": "With",
  "access.mine.waiting": "Waiting",
  "access.mine.granted": "Granted",
  "access.mine.refused": "Refused",
  "access.mine.overdue": "Overdue",
  "access.mine.nobody": "Nobody yet",
  "access.mine.answer": "They said",
  "access.mine.loading": "Loading your requests…",
  "access.mine.loadFailed": "Your requests could not be read.",
  "access.ask.title": "Ask for more",
  "access.ask.explain":
    "Building the thing is not a reason to hold the data in it. Ask for the smallest thing that lets you finish, for as long as you need it. It goes to whoever can grant it, is granted to you alone, and ends by itself.",
  "access.ask.what": "What you need",
  "access.ask.until": "Until",
  "access.ask.why": "Why you need it",
  "access.ask.whyHint": "What you are doing, and why this is the smallest thing that lets you do it",
  "access.ask.send": "Send the request",
  "access.ask.pickOne": "Pick what you need",
  "access.ask.pastDate": "Say a date in the future, or leave it open.",
  "access.ask.sent": "Sent. It is case {reference}.",
  "cases.kind.access": "Access request",
  "cases.kind.customer": "Customer",
  "cases.kind": "Kind",
  "cases.access.wants": "Asks for",
  "cases.access.until": "Until",
  "cases.access.grant": "Grant",
  "cases.access.refuse": "Refuse",
  "cases.access.own": "You cannot answer your own request.",
  "cases.access.explain":
    "Granted to this person alone, never to their team. It ends on the date they asked for, and anyone with the authority can cut it off before then.",
  "cases.priority": "Priority: {priority}",
  "cases.priority.low": "Low",
  "cases.priority.normal": "Normal",
  "cases.priority.high": "High",
  "cases.priority.urgent": "Urgent",
  "cases.unknownCaller": "Unknown caller",
  "cases.unknownCallerPhone": "Unknown caller · {phone}",
  "cases.customerWithPhone": "{name} · {phone}",
  "cases.ownedBy": "assigned to {name}",
  "cases.unassigned": "unassigned",
  "cases.closedBecause": "Closed · reason: {reason}",
  "cases.timeline.aria": "Case timeline",
  "cases.timeline.empty": "Nothing has been recorded on this case yet.",
  "cases.internal": "Internal",
  "cases.entry.call": "Call",
  "cases.entry.callWithDuration": "Call ({duration})",
  "cases.entry.note": "Internal note",
  "cases.entry.chatIn": "Customer message in the app",
  "cases.entry.chatOut": "Reply in the app",
  "cases.entry.smsIn": "SMS from the customer",
  "cases.entry.smsOut": "Reply by SMS",
  "cases.entry.smsOutTo": "SMS to {phone}",
  "cases.entry.emailIn": "Email from the customer",
  "cases.entry.emailOut": "Reply by email",
  "cases.sms.pending": "Sending",
  "cases.sms.sent": "Sent",
  "cases.sms.failed": "Not sent",
  "cases.action.claim": "Claim",
  "cases.action.reassign": "Reassign",
  "cases.action.link": "Link to a customer",
  "cases.action.relink": "Change customer",
  "cases.action.close": "Close",
  "cases.needs": "Needs {feature}",
  "cases.sending": "Sending…",
  "cases.cancel": "Cancel",
  "cases.reload": "Reload the case",
  "cases.reason": "Reason",
  "cases.reasonHint": "Recorded in the audit log with your name.",
  "cases.stale":
    "This case changed while you were looking at it. Reload it, check what changed, then try again: your text is kept.",
  "cases.claim.done": "{reference} is yours.",
  "cases.claim.taken": "Too late: {name} just claimed this case. Pick another one.",
  "cases.composer.aria": "Write on the case",
  "cases.composer.reply": "Reply",
  "cases.composer.note": "Internal note",
  "cases.composer.call": "Log a call",
  "cases.reply.noChannel": "No customer account is linked: link the customer to reply in the app, or call them back.",
  "cases.reply.notified": "The customer gets a notification in the app. Never an SMS.",
  "cases.reply.label": "Reply to {name}",
  "cases.reply.sendChat": "Send in the app",
  "cases.reply.doneChat": "Reply sent in the app.",
  "cases.reply.closed": "This case is closed: it takes no more replies.",
  "cases.reply.unassigned": "Claim this case to reply to the customer.",
  "cases.reply.notOwner": "Only the case's owner replies to the customer. Ask for it to be reassigned if needed.",
  "cases.refused.claimed": "Too late: someone else just claimed this case. Pick another one.",
  "cases.refused.closed": "This case was closed meanwhile. Reload it.",
  "cases.refused.notOwner": "This case belongs to someone else now. Reload it, or ask for it to be reassigned.",
  "cases.refused.noCustomer":
    "No customer account is linked: link the customer to reply in the app, or call them back.",
  "cases.note.hint": "Seen by the team only, never by the customer.",
  "cases.note.label": "Internal note",
  "cases.note.submit": "Add the note",
  "cases.note.done": "Internal note added.",
  "cases.call.label": "What was said (internal summary)",
  "cases.call.minutes": "Length (minutes)",
  "cases.call.submit": "Log the call",
  "cases.call.done": "Call logged.",
  "cases.reassign.title": "Reassign {reference}",
  "cases.reassign.from": "Currently assigned to {name}.",
  "cases.reassign.unassigned": "Nobody owns this case yet.",
  "cases.reassign.to": "New owner",
  "cases.reassign.choose": "Pick a team member",
  "cases.reassign.loading": "Loading the team…",
  "cases.reassign.toId": "Team member ID",
  "cases.reassign.idHint": "Without access to the team list, paste the member's ID.",
  "cases.reassign.submit": "Reassign",
  "cases.reassign.done": "Case reassigned to {name}.",
  "cases.close.title": "Close {reference}",
  "cases.close.body": "The customer can no longer reply to it; if they come back, a new case is opened.",
  "cases.close.submit": "Close the case",
  "cases.close.done": "{reference} is closed.",
  "cases.link.title": "Link {reference} to a customer",
  "cases.link.body": "Find the account of the customer who contacted support.",
  "cases.link.caller": "The caller gave {phone}. Find their account.",
  "cases.link.current": "Currently linked to {name}.",
  "cases.link.submit": "Link",
  "cases.link.done": "Case linked to {name}.",
  "cases.customer.search": "Find the customer",
  "cases.customer.placeholder": "Name, phone, email or ID",
  "cases.customer.searchButton": "Search",
  "cases.customer.searching": "Searching…",
  "cases.customer.none": "No account found.",
  "cases.customer.results": "Accounts found",
  "cases.customer.id": "Customer account ID",
  "cases.customer.idHint": "Searching needs {feature}: paste the account ID.",
  "cases.newCall.title": "Log a call",
  "cases.newCall.who": "Who called?",
  "cases.newCall.known": "A customer",
  "cases.newCall.unknown": "An unknown caller",
  "cases.newCall.phone": "Caller's number",
  "cases.newCall.phoneHint": "In international format, for example +224 621 23 45 67.",
  "cases.newCall.subject": "Subject",
  "cases.newCall.internal": "Internal: the customer never sees this summary.",
  "cases.newCall.priority": "Priority",
  "cases.newCall.submit": "Open the case",
  "cases.problem.reason": "Say why, in a few words.",
  "cases.problem.body": "Write something.",
  "cases.problem.tooLong": "Too long: {max} characters at most.",
  "cases.problem.minutes": "The length is a whole number of minutes, from 0 to {max}.",
  "cases.problem.phone": "The number must be in international format, for example +224621234567.",
  "cases.problem.pickCustomer": "Pick the customer.",
  "cases.problem.customerId": "This account ID isn't in the right format.",
  "cases.problem.pickStaff": "Pick who takes the case.",
  "cases.problem.staffId": "This member ID isn't in the right format.",
  "cases.problem.subject": "Give the case a short subject.",
  // ── Verifications ──
  "verifications.summary.open": "{count} waiting · the oldest for {age}",
  "verifications.summary.none": "No verification waiting",
  "verifications.summary.shown": "{count} verifications shown",
  "verifications.filter.status": "Status",
  "verifications.filter.status.open": "All waiting",
  "verifications.filter.status.submitted": "To review",
  "verifications.filter.status.flagged": "Banned document",
  "verifications.filter.status.approved": "Approved",
  "verifications.filter.status.rejected": "Rejected",
  "verifications.filter.document": "Document type",
  "verifications.filter.document.all": "All",
  "verifications.filter.document.national_id": "ID card",
  "verifications.filter.document.passport": "Passport",
  "verifications.queue.aria": "Verification queue, oldest first",
  "verifications.queue.loading": "Loading the queue…",
  "verifications.queue.loadFailed": "The queue could not be loaded.",
  "verifications.queue.emptyOpen": "No verification is waiting: the queue is empty.",
  "verifications.queue.empty": "No verification matches these filters.",
  "verifications.queue.waited": "Time waited since it was sent",
  "verifications.queue.end": "End of the queue.",
  "verifications.flag.banned": "Banned document",
  "verifications.status.submitted": "To review",
  "verifications.status.flagged": "Banned document",
  "verifications.status.approved": "Approved",
  "verifications.status.rejected": "Rejected",
  "verifications.document.national_id": "ID card",
  "verifications.document.passport": "Passport",
  "verifications.side.front": "Front",
  "verifications.side.back": "Back",
  "verifications.side.selfie": "Selfie photo",
  "verifications.detail.pick": "Pick a verification in the queue to review it.",
  "verifications.detail.loading": "Loading the verification…",
  "verifications.detail.loadFailed": "This verification could not be loaded.",
  "verifications.detail.notFound": "No verification has this id. Reload the queue.",
  "verifications.detail.noPhone": "no phone",
  "verifications.detail.submitted": "sent on {date}",
  "verifications.detail.versionTitle": "Version reviewed: a decision applies to this version only",
  "verifications.banned.title": "Banned document",
  "verifications.banned.body":
    "This number matches a document banned on {date} · reason: {reason}. It can never be approved: reject the verification.",
  "verifications.banned.bodyNoReason":
    "This number matches a banned document. It can never be approved: reject the verification.",
  "verifications.banned.notTold": "The pro is not told that their document is banned.",
  "verifications.images.aria": "ID documents",
  "verifications.images.show": "Show the documents",
  "verifications.images.showAgain": "Show again",
  "verifications.images.loading": "Loading…",
  "verifications.images.notice":
    "Every view is recorded in the audit log, under your name, and appears in the pro's history. The links expire after 2 minutes.",
  "verifications.images.recorded": "View recorded in the audit log · links valid until {time}",
  "verifications.images.hide": "Hide",
  "verifications.images.expired":
    "The links expired and the images were removed from the screen. Showing them again is recorded too.",
  "verifications.images.locked": "ID images require",
  "verifications.images.deleted": "The images were deleted on {date}, as the retention rule requires.",
  "verifications.col.field": "Field",
  "verifications.col.read": "Read on the document",
  "verifications.col.typed": "Typed by the pro",
  "verifications.col.match": "Match",
  "verifications.field.name": "Name",
  "verifications.field.dateOfBirth": "Born on",
  "verifications.field.expiresOn": "Expires on",
  "verifications.field.documentNumber": "Document no.",
  "verifications.notAsked": "not asked",
  "verifications.match.check": "Check on the image",
  "verifications.match.same": "Matches",
  "verifications.match.different": "Doesn't match",
  "verifications.match.readOnly": "Read only",
  "verifications.match.expired": "Expired",
  "verifications.match.valid": "Valid",
  "verifications.match.banned": "Banned",
  "verifications.match.notBanned": "Not banned",
  "verifications.last4Hint": "Only the number's last 4 characters are kept ({last4}): compare them with the image.",
  "verifications.standing.title": "The pro's account today",
  "verifications.standing.verificationStatus": "Verification status",
  "verifications.standing.approvalStatus": "Profile approval",
  "verifications.standing.isVerified": "“Verified” badge",
  "verifications.standing.status.unverified": "Not verified",
  "verifications.standing.status.pending": "Under review",
  "verifications.standing.status.verified": "Verified",
  "verifications.standing.status.rejected": "Rejected",
  "verifications.standing.approval.pending": "To approve",
  "verifications.standing.approval.approved": "Approved",
  "verifications.standing.approval.rejected": "Not approved",
  "verifications.yes": "Yes",
  "verifications.no": "No",
  "verifications.previous.title": "Earlier submissions",
  "verifications.previous.none": "This pro's first submission.",
  "verifications.approve.button": "Approve the pro",
  "verifications.reject.button": "Reject",
  "verifications.decided": "This verification has already been decided.",
  "verifications.approve.banned": "Can't be approved: the number matches a banned document. Reject the verification.",
  "verifications.approve.locked":
    "Approving also requires APPROVE_PROS_STAFF_FEATURE_ENABLEMENT, which your roles don't include.",
  "verifications.decided.approved": "Approved by {name} on {date}.",
  "verifications.decided.rejected": "Rejected by {name} on {date}.",
  "verifications.decided.reasonCode": "Reason: {reason}",
  "verifications.decided.reason": "Team note: “{reason}”",
  "verifications.outcome.approved": "{name} is approved: their profile is verified, and they can be found and hired.",
  "verifications.outcome.rejected": "Verification refused. The pro is told in the app, in their language.",
  "verifications.approve.title": "Approve {name}",
  "verifications.approve.body":
    "The document is accepted and the pro becomes verified: they can be found and hired. The decision is recorded in the audit log with your reason.",
  "verifications.approve.reason": "Reason (recorded in the audit log)",
  "verifications.approve.hint": "E.g. “Card readable, selfie matches”.",
  "verifications.approve.submit": "Approve the pro",
  "verifications.approve.tooShort": "Give a reason of at least {min} characters.",
  "verifications.reject.title": "Reject {name}'s verification",
  "verifications.reject.body": "The pro can send a new document from the app.",
  "verifications.reject.preset": "Why it is refused",
  "verifications.reject.presetNone": "Pick a reason…",
  "verifications.reject.code.photo_unreadable": "Photo unreadable",
  "verifications.reject.code.selfie_mismatch": "Selfie doesn't match",
  "verifications.reject.code.document_expired": "Document expired",
  "verifications.reject.code.document_not_accepted": "Document not accepted",
  "verifications.reject.code.other": "Other",
  "verifications.reject.reason": "Note for the team (optional)",
  "verifications.reject.noteHint":
    "Never shown to the pro: they get the message for the reason you picked, in the app, in their language.",
  "verifications.reject.submit": "Refuse the verification",
  "verifications.reject.pickCode": "Pick why the ID is refused.",
  "verifications.reason.tooLong": "The reason can have at most {max} characters.",
  "verifications.reason.count": "{count} of {max} characters",
  "verifications.dialog.version": "Decision on version {version}",
  "verifications.dialog.cancel": "Cancel",
  "verifications.dialog.sending": "Sending…",
  "verifications.error.stale":
    "This verification changed while you were looking at it (someone else may have decided it). Reload to see the current version, then decide again.",
  "verifications.error.approveRefused":
    "Approval refused: your roles don't include REVIEW_VERIFICATIONS and APPROVE_PROS, or the document was just banned. Reload.",
  "verifications.error.rejectRefused":
    "Can't reject: your roles no longer include REVIEW_VERIFICATIONS. Ask a manager for access.",
  "verifications.error.imagesRefused":
    "Images refused: your roles no longer include VIEW_ID_IMAGES. This attempt is recorded in the audit log.",
  "verifications.error.approveInvalid":
    "Approval refused: the verification is already decided, the document is banned, or the reason is invalid. Reload and check.",
  "verifications.error.rejectInvalid":
    "Rejection refused: the verification is already decided, or the note is over {max} characters.",
  "verifications.error.imagesDeleted":
    "The images are no longer available: they were deleted under the retention rule, or the verification can't be found.",
  "verifications.error.outcomeUnknown":
    "No answer from the server: the decision may have been saved. Try again: the same click can't be applied twice.",
};
